// ─── Button component ──────────────────────────────────────────────────────────

import React, { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '../../utils/helpers';
import type { ButtonVariant, ButtonSize } from '../../types';
import Spinner from './Spinner';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  fullWidth?: boolean;
}

// `danger` paints with `errorSolid`, not `error`: white on the base #EF4444 is
// 3.76:1, under WCAG AA's 4.5:1 for the 12-13px labels these buttons carry.
const variantClasses: Record<ButtonVariant, string> = {
  primary:   'bg-primary hover:bg-primaryHover text-primaryFg shadow-card focus-visible:ring-primary/40 ring-1 ring-inset ring-white/10',
  secondary: 'bg-surface hover:bg-surfaceMuted text-textMain border border-borderLight hover:border-borderMedium shadow-sm focus-visible:ring-borderMedium',
  success:   'bg-success hover:bg-successHover text-white shadow-card focus-visible:ring-success/40 ring-1 ring-inset ring-white/15',
  danger:    'bg-errorSolid hover:brightness-110 text-white shadow-card focus-visible:ring-errorSolid/40 ring-1 ring-inset ring-white/15',
  ghost:     'bg-transparent hover:bg-surfaceMuted text-textMain focus-visible:ring-borderMedium',
  outline:   'bg-surface border border-borderLight hover:bg-surfaceMuted hover:border-borderMedium text-textMain focus-visible:ring-borderMedium',
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-[12px] rounded-md gap-1.5',
  md: 'h-9 px-4 text-[13px] rounded-md gap-2',
  lg: 'h-11 px-6 text-[13px] rounded-lg gap-2',
};

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  leftIcon,
  rightIcon,
  fullWidth = false,
  disabled,
  children,
  className,
  ...rest
}) => {
  return (
    <button
      disabled={disabled || isLoading}
      aria-busy={isLoading || undefined}
      className={cn(
        'inline-flex items-center justify-center font-semibold tracking-tight transition-all duration-150',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none',
        'active:translate-y-[0.5px]',
        variantClasses[variant],
        sizeClasses[size],
        fullWidth && 'w-full',
        className
      )}
      {...rest}
    >
      {isLoading ? (
        <Spinner
          size="sm"
          label={null}
          color={variant === 'primary' || variant === 'success' || variant === 'danger' ? 'white' : 'navy'}
        />
      ) : (
        leftIcon
      )}
      {children}
      {!isLoading && rightIcon}
    </button>
  );
};

export default Button;
